import { Box } from '@mui/material'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import { CanvasAlignment } from '@/components/canvas/constants'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useDualCanvasSize } from '@/hooks/useDualCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import KonvaNewWashBake from '@/pages/stainingStudio/newWashbake/KonvaNewWashBake'
import {
  useGenerateRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  removeOpaquerStainingTarget,
  setOpaquerStainingFrameworkKey,
  setOpaquerStainingOutput,
} from '@/store/pfmCrown'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  getProtocolValue,
  hasNonEmptyOutputDualCanvas,
  isImageCalibrated,
  saveRecipeGeneric,
} from '@/utils/helper'
import { toLabColor } from '@/utils/matisseV2/toLabColor'

import OpaquerStainingPanel from './OpaquerStainingPanel'

const OpaquerStaining = () => {
  const { t } = useTranslation()
  const alignment = CanvasAlignment.VERTICAL
  const dispatch = useDispatch()
  const chipsContainerRef = useRef(null)
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipMessage, setTooltipMessage] = useState('')
  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [circleColor, setCircleColor] = useState<string>('#ffffff')
  const [activeCanvas, setActiveCanvas] = useState<PLACEMENT | null>(null)

  const [generateRecipe] = useGenerateRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const selectedColor = useAppSelector((store) => store.app.selectedColor)

  const opaquerStainingOutput = useAppSelector(
    (state) => state.pfmCrown.opaquerStainingOutput,
  )

  const handleDeleteWashbake = useCallback(
    (selectedOpaquerFrameworkKey: string) => {
      if (!dualCanvasImage) return

      dispatch(setOpaquerStainingFrameworkKey(''))
      dispatch(
        removeOpaquerStainingTarget({ key: selectedOpaquerFrameworkKey }),
      )
    },
    [dispatch, dualCanvasImage],
  )

  const [leftElements, rightElements] = useMemo(() => {
    if (!opaquerStainingOutput) return [[], []]

    return [
      Object.values(opaquerStainingOutput[PLACEMENT.LEFT] || {})
        .map((recipe: any) => ({
          component: (
            <KonvaNewWashBake
              input={recipe.input}
              output={recipe.output}
              onDelete={handleDeleteWashbake}
              tooltipPlacement={PLACEMENT.LEFT}
            />
          ),
          id: recipe.input?.fullmonolithiceffect,
        }))
        .filter((recipe) => recipe.component),
      Object.values(opaquerStainingOutput[PLACEMENT.RIGHT] || {})
        .map((recipe: any) => ({
          component: (
            <KonvaNewWashBake
              input={recipe.input}
              output={recipe.output}
              onDelete={handleDeleteWashbake}
              tooltipPlacement={PLACEMENT.RIGHT}
            />
          ),
          id: recipe.input?.fullmonolithiceffect,
        }))
        .filter((recipe) => recipe.component),
    ]
  }, [opaquerStainingOutput])

  const { containerRef, dimensions } = useDualCanvasSize(
    alignment,
    (chipsContainerRef as any)?.current?.clientHeight,
  )

  const placement = useAppSelector((state) => state.app.placement)
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const selectedOpaquerFrameworkKey = useAppSelector(
    (state) => state.pfmCrown.selectedOpaquerFrameworkKey,
  )

  const opaquerStainingBrand = useAppSelector(
    (state) => state.pfmCrown.opaquerStainingBrand,
  )

  const availableSpace = useAppSelector(
    (state) => state.pfmCrown.availableSpace,
  )

  const onImageClick = (
    type: string,
    placement: PLACEMENT,
    pos?: { x: number; y: number },
    labColor?: { L: number; a: number; b: number },
  ) => {
    const imageId = (dualCanvasImage as any)?.[placement as any]?.id
    if (!imageId || !labColor) return

    if (!selectedOpaquerFrameworkKey) return

    const existingTarget =
      opaquerStainingOutput?.[PLACEMENT.LEFT]?.[selectedOpaquerFrameworkKey]
        ?.input?.targetcolor ||
      opaquerStainingOutput?.[PLACEMENT.RIGHT]?.[selectedOpaquerFrameworkKey]
        ?.input?.targetcolor

    if (!existingTarget) {
      dispatch(
        setOpaquerStainingOutput({
          placement,
          key: selectedOpaquerFrameworkKey,
          data: {
            input: {
              targetcolor: [selectedColor.L, selectedColor.a, selectedColor.b],
              x: pos?.x,
              y: pos?.y,
              fullmonolithiceffect: selectedOpaquerFrameworkKey,
            },
          },
        }),
      )
      setTooltipOpen(true)
      setTooltipMessage(
        'Next, click on the spot on the framework on the aesthetic model.',
      )
      return
    }

    if (!opaquerStainingBrand) {
      toast.error(t('washBake.selectStainingBrand'))
      return
    }

    const backgroundcolor = toLabColor([
      selectedColor.L,
      selectedColor.a,
      selectedColor.b,
    ])
    const targetcolor = toLabColor(existingTarget)
    const space =
      availableSpace === null ||
      availableSpace === undefined ||
      availableSpace === ''
        ? undefined
        : Number(availableSpace)

    const payload = {
      stainingbrand: opaquerStainingBrand,
      protocol: getProtocolValue((dualCanvasImage as any)?.[placement as any]),
      ...(backgroundcolor ? { backgroundcolor } : {}),
      ...(targetcolor ? { targetcolor } : {}),
      ...(space !== undefined && Number.isFinite(space)
        ? { availablespace_from_framework: space }
        : {}),
    }
    toast.info(t('recipeGenerate.inProgress'))
    generateRecipe({
      caseId,
      recipePath: 'staining-layering',
      data: payload,
    })
      .unwrap()
      .then((res) => {
        const { generation_id, ...output } = res ?? {}
        setTooltipOpen(false)
        dispatch(
          setOpaquerStainingOutput({
            placement,
            key: selectedOpaquerFrameworkKey,
            data: {
              input: {
                ...payload,
                x: pos?.x,
                y: pos?.y,
                fullmonolithiceffect: selectedOpaquerFrameworkKey,
              },
              output,
              generation_id,
            },
          }),
        )
        toast.success(t('washBake.recipeGenerated'))
        dispatch(setOpaquerStainingFrameworkKey(''))
      })
      .catch(() => {
        toast.error(t('washBake.recipeGenerationFailed'))
      })
  }

  const isBothSideImageSelected =
    !!dualCanvasImage?.[PLACEMENT.LEFT]?.id &&
    !!dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const isLeftImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.LEFT],
  )

  const isRightImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.RIGHT],
  )

  const isBothImageCalibrated = isLeftImageCalibrated && isRightImageCalibrated

  const onSaveAsImage = () => {
    generateImage(combinedRef as any, 'Opaquer Staining-Recipe.png')
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const getWashBakeOutputsOnly = (opaquerStainingOutput: any) => {
    const cleaned: Record<string, any> = {}

    if (opaquerStainingOutput?.[PLACEMENT.LEFT]) {
      Object.entries(opaquerStainingOutput[PLACEMENT.LEFT]).forEach(
        ([key, recipe]: any) => {
          if (recipe.output) {
            cleaned[key.toUpperCase()] = recipe.output
          }
        },
      )
    }

    if (opaquerStainingOutput?.[PLACEMENT.RIGHT]) {
      Object.entries(opaquerStainingOutput[PLACEMENT.RIGHT]).forEach(
        ([key, recipe]: any) => {
          if (recipe.output) {
            cleaned[key.toUpperCase()] = recipe.output
          }
        },
      )
    }

    return cleaned
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    const outputsOnly = getWashBakeOutputsOnly(opaquerStainingOutput)

    await saveRecipeGeneric({
      caseId: targetCaseId,
      dualCanvasImage,
      recipeName,
      output: outputsOnly,
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      recipeType: 'staining_layering',
      closeDialog: () => setSaveRecipedialogOpen(false),
      inputType: 'dual',
    })
  }

  const onSaveAsPDF = async () => {
    const outputsOnly = getWashBakeOutputsOnly(opaquerStainingOutput)

    await saveRecipeGeneric({
      caseId,
      dualCanvasImage,
      recipeName,
      output: outputsOnly,
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'staining_layering',
      inputType: 'dual',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      disabled={hasNonEmptyOutputDualCanvas(opaquerStainingOutput)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <WorkFlowTab width={dimensions.width * 2} />
        <Box display="flex" flexGrow={1} ref={containerRef} position="relative">
          {!imageEdit ? (
            <>
              <Box ref={combinedRef} display="flex">
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.LEFT]) ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.LEFT}
                  type={dualCanvasImage?.[PLACEMENT.LEFT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.LEFT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={leftElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_height}
                  isActiveCircle={!!selectedOpaquerFrameworkKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.RIGHT}
                  type={dualCanvasImage?.[PLACEMENT.RIGHT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.RIGHT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={rightElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_height}
                  isActiveCircle={!!selectedOpaquerFrameworkKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
              </Box>
              {isBothSideImageSelected && isBothImageCalibrated && (
                <OpaquerStainingPanel
                  setTooltipOpen={setTooltipOpen}
                  setTooltipMessage={setTooltipMessage}
                  dimensions={dimensions}
                />
              )}
              {containerRef.current && (
                <SnackbarTooltip
                  containerRef={containerRef}
                  message={tooltipMessage}
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  placement="top"
                  offset={[0, -100]}
                  autoHideDuration={10000}
                  maxWidth={500}
                />
              )}
            </>
          ) : (
            <ImageEdit
              selectedImage={
                placement === PLACEMENT.LEFT
                  ? dualCanvasImage[PLACEMENT.LEFT]
                  : dualCanvasImage[PLACEMENT.RIGHT]
              }
            />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default OpaquerStaining
